/**
 * ISK (Icelandic Króna) number formatting utilities.
 *
 * Icelandic groups thousands with a DOT: 1.000.000. We do the grouping
 * ourselves instead of asking Intl, because `Intl.NumberFormat('is-IS')`
 * silently falls back to comma grouping wherever the ICU locale data for
 * Icelandic is missing (Android WebView, trimmed Chromium builds), and
 * money is the one place a wrong separator is never acceptable (iter368).
 */

const group = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Format a number for display with Icelandic thousands separator (dots).
 * @param {number|string} value - The number to format
 * @returns {string} Formatted string like "1.000.000"
 */
export const formatISK = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/\./g, '')) : Number(value);
  if (isNaN(num)) return '';
  const rounded = Math.round(num);
  return (rounded < 0 ? '-' : '') + group(String(Math.abs(rounded)));
};

/** Alias used across the money UI. */
export const formatKr = formatISK;

/**
 * Parse a formatted ISK string back to a number.
 * Handles both "1.000.000" and "1000000" formats.
 * @param {string} value - The formatted string
 * @returns {number} The numeric value
 */
export const parseISK = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(/\./g, '').replace(/\s/g, '');
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? 0 : num;
};

/**
 * Handle ISK input — formats on the fly as user types.
 * Returns the raw numeric value and the formatted display string.
 * @param {string} inputValue - Raw input from the text field
 * @returns {{ raw: number, display: string }}
 */
export const handleISKInput = (inputValue) => {
  // Strip everything except digits
  const digits = inputValue.replace(/[^\d]/g, '');
  if (!digits) return { raw: 0, display: '' };
  const num = parseInt(digits, 10);
  return { raw: num, display: formatISK(num) };
};

/**
 * Format kennitala as "xxxxxx xxxx" (6 digits, space, 4 digits).
 * @param {string} value - Raw input
 * @returns {string} Formatted kennitala
 */
export const formatKennitala = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 6) return digits;
  return digits.slice(0, 6) + ' ' + digits.slice(6);
};

/**
 * Parse kennitala back to digits only.
 * @param {string} value - Formatted kennitala
 * @returns {string} Raw digits
 */
export const parseKennitala = (value) => {
  return value.replace(/\D/g, '').slice(0, 10);
};
